import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { usePostHog } from '@posthog/react';

import { authService } from '@/services/auth';
import { userService } from '@/services/user';
import { User } from '@/types/user';
import { LoginCredentials, AuthError } from '@/types/auth';
import { isAnalyticsConsentGranted } from '@/lib/cookieyes';
import { initPostHog, isPostHogCapturing, posthog } from '@/lib/posthog';
import { identifyPostHogUser } from '@/lib/posthogIdentity';
import { trackEvent } from '@/lib/trackEvent';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  loginLoading: boolean;
  user: User | null;
  login: (_credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  error: string | null;
  clearError: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const posthogClient = usePostHog();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadUserData = async (): Promise<User | null> => {
    try {
      const userData = await userService.getCurrentUser();
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Failed to load user data:', error);
      authService.logout();
      setIsAuthenticated(false);
      setUser(null);
      return null;
    }
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      setLoginLoading(true);
      setError(null);
      await authService.login(credentials);
      const userData = await loadUserData();
      setIsAuthenticated(true);
      if (isAnalyticsConsentGranted()) {
        initPostHog();
      }
      if (userData) {
        identifyPostHogUser(userData);
      }
      trackEvent('user_logged_in');
    } catch (err: any) {
      const authError = err as AuthError;
      setError(authError.message);
      throw authError;
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = () => {
    trackEvent('user_logged_out');
    if (isPostHogCapturing()) {
      posthog.reset();
    }
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
  };

  const clearError = () => {
    setError(null);
  };

  const refreshUser = async () => {
    if (!authService.isAuthenticated()) {
      setIsAuthenticated(false);
      setUser(null);
      return;
    }

    const userData = await loadUserData();
    setIsAuthenticated(!!userData);
  };

  const loadAuth = async () => {
    const authenticated = authService.isAuthenticated();
    if (authenticated) await loadUserData();
    setIsAuthenticated(authenticated);
    setIsLoading(false);
  };

  useEffect(() => {
    loadAuth();
  }, []);

  useEffect(() => {
    if (!user || !isPostHogCapturing()) return;
    identifyPostHogUser(user);
  }, [user, posthogClient]);

  const value = {
    isAuthenticated,
    isLoading,
    loginLoading,
    user,
    login,
    logout,
    error,
    clearError,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
